import { ButtonLink } from '@/components/ui';
import { Wordmark } from '@/components/brand/Wordmark';
import heroAvatar from '@/assets/brand/hero-avatar.webp';
import { paths } from '@/routes/paths';

import { ReopenAlertForm } from './ReopenAlertForm';

export interface HeroProps {
  /** Offline disables the call to action: starting an order needs the API. */
  online: boolean;
  /** False while the shop is shut. Disables the CTA for the same reason. */
  acceptingOrders?: boolean;
  /** The operator's notice, shown in place of the CTA while shut. */
  closedMessage?: string | null;
}

/**
 * The one moment on the site that looks like the poster.
 *
 * Everything past this block is a checkout, and a checkout should be calm. So
 * the comic devices are concentrated here and do not follow the customer into
 * the flow — the brand gets one loud room, then gets out of the way.
 *
 * The halftone, ray burst and speed lines are CSS gradients, not images: they
 * are three declarations, they scale to any viewport without a second asset,
 * and they cost nothing on a slow connection. Every one of them is
 * `aria-hidden` and `pointer-events-none` — they are texture, not content.
 *
 * Gold-on-black is used freely here and only here. The 1.75:1 problem that
 * shapes the rest of the palette is gold on *white*; against the near-black
 * plate the same gold is 11.3:1, which is why `text-brand` is correct in this
 * file and wrong almost everywhere else.
 */
export function Hero({ online, acceptingOrders = true, closedMessage }: HeroProps) {
  return (
    <section className="relative -mx-4 overflow-hidden bg-surface-inverse text-ink-inverse sm:mx-0 sm:rounded-card">
      {/* Ray burst behind the character, anchored where the artwork's own
          radiating lines converge. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-16 h-[26rem] w-[26rem] opacity-[0.13]"
        style={{
          background:
            'repeating-conic-gradient(from 8deg at 62% 42%, rgb(252 184 20) 0deg 1.6deg, transparent 1.6deg 11deg)',
          // Hollow centre: the rays should read as glow behind the badge, not
          // as a sunburst with a findable origin point.
          maskImage:
            'radial-gradient(circle at 62% 42%, transparent 6%, black 26%, transparent 72%)',
          WebkitMaskImage:
            'radial-gradient(circle at 62% 42%, transparent 6%, black 26%, transparent 72%)',
        }}
      />
      {/* Halftone screen — the single most recognisable thing about the print. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: 'radial-gradient(circle, rgb(252 184 20 / 0.9) 1px, transparent 1.3px)',
          backgroundSize: '9px 9px',
          maskImage: 'linear-gradient(115deg, black 0%, transparent 55%)',
          WebkitMaskImage: 'linear-gradient(115deg, black 0%, transparent 55%)',
        }}
      />

      <div className="relative flex flex-col gap-5 px-5 pb-7 pt-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {/* The wordmark IS the page's identity here, so it carries the
                accessible name and the h1 below is the offer, not the brand. */}
            <Wordmark width={208} className="max-w-full" />
          </div>
          {/* The avatar badge, not the full character logo: that artwork has
              the wordmark baked into it, which would put PRINTOMAN on screen
              three times in one viewport. Decorative, so hidden from assistive
              tech — but never hidden visually. It used to be `hidden xs:block`,
              which silently removed it on every 360px Android phone; the
              wordmark beside it is fluid now, so both fit at any width. */}
          <img
            src={heroAvatar}
            alt=""
            aria-hidden="true"
            width={72}
            height={79}
            loading="eager"
            decoding="async"
            className="-mt-1 w-16 shrink-0 select-none xs:w-[72px]"
          />
        </div>

        {/* A <p>, not an <h1>. "You click. We print. We deliver." is a slogan,
            not a document heading — and the shell already renders the page's
            one h1 (visually hidden here) which is also the focus target on
            navigation. A second h1 would give the page two, and would leave a
            screen-reader user landing on a heading that is not the one the
            focus manager moved to. */}
        <p className="font-display text-[1.7rem] font-extrabold leading-[1.15] tracking-tight text-ink-inverse lg:text-5xl">
          {/* Each clause is its own line: the three-beat rhythm is the line, and
              letting "We print." wrap mid-phrase throws it away. */}
          <span className="block">You click.</span>
          <span className="block text-brand">We print.</span>
          <span className="block">We deliver.</span>
        </p>
        <p className="-mt-3 max-w-prose text-sm text-ink-inverse/80 lg:text-base">
          Upload a PDF and see the exact price — printing, delivery and GST — before you pay a
          rupee.
        </p>

        {/* Full width on a phone, where the button IS the thumb target and the
            column is 32rem anyway. On a wide hero a 1300px button stops reading
            as a button, so it shrinks to its label. */}
        {/* Closed: say so HERE, where the eye already is, and disable the
            button. Letting someone upload, choose paper and sign in before
            telling them we cannot sell it to them is the rude version. The
            refusal itself is enforced at checkout — this is the courtesy. */}
        {acceptingOrders ? (
          <div className="lg:max-w-xs">
            <ButtonLink to={paths.order.root} size="lg" fullWidth disabled={!online}>
              Upload your file
            </ButtonLink>
          </div>
        ) : (
          <div className="rounded-card border border-white/20 bg-white/5 p-4 lg:max-w-xl">
            <p className="font-semibold text-brand">We are not taking orders right now</p>
            {closedMessage ? (
              <p className="mt-1 text-sm text-ink-inverse/80">{closedMessage}</p>
            ) : null}
            {/* A closed shop is otherwise a dead end: someone who needed
                printing today reads this, leaves, and finds another shop. */}
            <ReopenAlertForm />
          </div>
        )}

        <ol className="grid grid-cols-3 gap-2 border-t border-white/10 pt-4 lg:max-w-2xl lg:gap-6">
          {[
            { n: '1', label: 'You upload', note: 'A PDF, in a few taps.' },
            { n: '2', label: 'We print', note: 'Your paper, your way.' },
            { n: '3', label: 'We deliver', note: 'To your door.' },
          ].map((step) => (
            <li key={step.n} className="flex flex-col gap-1">
              <span
                aria-hidden="true"
                className="grid h-6 w-6 place-items-center rounded-full bg-brand font-display text-xs font-black text-brand-contrast"
              >
                {step.n}
              </span>
              <span className="font-display text-xs font-bold uppercase tracking-wide text-ink-inverse">
                {step.label}
              </span>
              <span className="text-[0.7rem] leading-snug text-ink-inverse/65">{step.note}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
